import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productCategories, products } from '@/mocks/products';
import ProductCard from '@/components/feature/ProductCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categoriesWithProducts = productCategories.filter((cat) =>
    products.some((p) => p.categoryId === cat.id)
  );

  let filteredProducts = products;

  if (activeCategory !== 'all') {
    filteredProducts = products.filter((p) => p.categoryId === activeCategory);
  }

  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredProducts = filteredProducts.filter((p) =>
      p.name.toLowerCase().includes(query)
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full pt-20">
        {/* Hero section */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h1 className="text-foreground-900 text-2xl md:text-4xl font-bold mb-3">
              Product Catalog
            </h1>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
              Browse all products from AllChinaBuy Spreadsheet. Click any item to view full details and purchase on the main platform.
            </p>
          </div>

          {/* Search bar */}
          <div className="max-w-xl mx-auto mb-6">
            <div className="relative flex items-center">
              <i className="ri-search-line absolute left-4 text-foreground-400 text-lg"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products by name..."
                className="w-full pl-11 pr-4 py-3 rounded-md border border-background-200 text-foreground-900 placeholder:text-foreground-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-background-50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 text-foreground-400 hover:text-foreground-700 transition-colors"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>
          </div>

          {/* Category filter tabs */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 border ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                  : 'bg-background-50 text-foreground-700 border-background-200 hover:bg-primary-50 hover:text-primary-700'
              }`}
            >
              All Products ({products.length})
            </button>
            {categoriesWithProducts.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 border ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                    : 'bg-background-50 text-foreground-700 border-background-200 hover:bg-primary-50 hover:text-primary-700'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
                <span className="text-xs font-medium opacity-80">
                  ({products.filter((p) => p.categoryId === cat.id).length})
                </span>
              </button>
            ))}
          </div>

          {/* Results count */}
          <div className="mb-4 text-center">
            <p className="text-foreground-500 text-sm">
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Product grid */}
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-line text-3xl"></i>
              </div>
              <p className="text-foreground-700 text-lg font-semibold mb-2">
                No products found
              </p>
              <p className="text-foreground-500 text-sm">
                Try adjusting your search or category filter
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ProductsPage;